import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil, filter } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { IReportData } from '../../models/reports/IReportData';
import { ReportCheckboxes } from '../../models/reports/ReportCheckboxes';
import { ReportsCommonService } from '../../services/reports/reports-common/reports-common.service';
import { SaveMultiPdfDialogComponent } from '../dialogs/reports/save-multi-pdf-dialog/save-multi-pdf-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { SaveMultiPdfDialogData } from '../../models/reports/SaveMultiPdfDialogData';
import { SaveMultiPdfDialogResult } from '../../models/reports/SaveMultiPdfDialogResult';
import { LoadReportGroupPayload } from '../../models/reports/LoadReportGroupPayload';
import { ReportEnginePayload } from '../../models/reports/ReportEnginePayload';
import { CommonFunctionsService } from '../../services/common-functions.service';
import { CommonDataService } from '../../services/common-data.service';
import { SaveMultiPdfConfirmationDialog } from '../dialogs/reports/save-multi-pdf-confirmation-dialog/save-multi-pdf-confirmation-dialog';
import { firstValueFrom } from "rxjs";
import { reportTranslationKeys } from '../../models/reports/ReportTranslationKeys';
import { ISaveMultiPDFConfirmationDialogData } from '../../models/reports/ISaveMultiPDFConfirmationDialogData';
import { CONSTANTS } from '../../Constants/Constants';


@Component({
  selector: 'app-reports',
  imports: [],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss'
})
export class ReportsComponent implements AfterViewInit, OnDestroy, OnInit {
  private _reportFrame: ElementRef<HTMLIFrameElement> | null = null;

  @ViewChild('reportFrame')
  set reportFrame(ref: ElementRef<HTMLIFrameElement>) {
    if (this._reportFrame?.nativeElement) {
      this._reportFrame.nativeElement.onload = null;
    }
    this._reportFrame = ref ?? null;
    if (ref?.nativeElement) {
      this.isIframeLoaded = false;
      ref.nativeElement.onload = () => {
        this.isIframeLoaded = true;
        this.shouldRehydrateMultiPdfState = true;
        this.sendReportTranslations();
        if (this.pendingReportData) {
          this.loadReportData(this.pendingReportData);
          this.pendingReportData = null;
        }
      };
    } else {
      this.isIframeLoaded = false;
    }
  }
  
  public showIframe = false;

  // Sample data imported from sampleData.js
  private destroy$ = new Subject<void>();
  private isIframeLoaded = false;
  private pendingReportData: IReportData | null = null;
  private lastStandardReportData: IReportData | null = null;
  private isRestoringFromMultiPdf = false;
  private shouldRehydrateMultiPdfState = false;
  public reportCheckboxes: ReportCheckboxes ;

  constructor(
    private readonly reportsCommonService: ReportsCommonService,
    private readonly dialog: MatDialog,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly translate: TranslateService,
    private readonly commonDataService: CommonDataService
  ) {}

  ngOnInit(): void {
    this.subscribeSelectedReport();
    this.subscribeAddToMultiPdfList();
    this.subscribeMultiPdfReportData();
    this.subscribeLoadReportGroup();
    this.initializeCheckboxStatesFromService();
    this.subscribeEstimateAvailability();
  }

  private initializeCheckboxStatesFromService(): void {
    this.reportCheckboxes = this.reportsCommonService.getReportsCheckboxes();
  }

  private subscribeEstimateAvailability(): void {
    this.commonDataService.projectObjectDataAvailable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(available => {
        this.showIframe = available;
        if (!available) {
          this.pendingReportData = null;
          this.isIframeLoaded = false;
          this.stopReportLoadingSpinner();
        }
      });
  }

  private subscribeSelectedReport(): void {
    this.reportsCommonService.selectedReport$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
      (reportData) => {
        if (reportData) {
          // Store last standard report data (non-Multi-PDF) for restoration on cancel
          if (!reportData.isMultiPdf) {
            this.lastStandardReportData = reportData;
          }
          
          if (this.isIframeLoaded) {
            this.loadReportData(reportData);
          } else {
            this.pendingReportData = reportData;
          }
        }
      }
    );
  }

  /**
   * Subscribe to add reports to Multi-PDF list events and forward to iframe
   */
  private subscribeAddToMultiPdfList(): void {
    this.reportsCommonService.addToMultiPdfList$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
      (reportData) => {
        if (reportData && this.isIframeLoaded) {
          this.sendAddToMultiPdfMessage(reportData.key, reportData.name);
        }
      }
    );
  }

  /**
   * Send message to iframe to add a report to the Multi-PDF selected list
   */
  private sendAddToMultiPdfMessage(reportKey: string, translatedName: string): void {
    
    const message: ReportEnginePayload = {
      type: 'ADD_TO_MULTI_PDF_LIST',
      payload: { reportKey, translatedName }
    };
    
    this.sendMessageToIframe(message);
  }
  
  /**
   * Subscribe to Multi-PDF report data events and forward to iframe
   */
  private subscribeMultiPdfReportData(): void {
    this.reportsCommonService.multiPdfReportData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
      (data) => {
        if (data && this.isIframeLoaded) {
          this.sendMultiPdfReportDataMessage(data.reportKey, data.reportData);
        }
      }
    );
  }
  
  /**
   * Send Multi-PDF report data to iframe for collection
   */
  private sendMultiPdfReportDataMessage(reportKey: string, reportData: IReportData): void {

    const message: ReportEnginePayload = {
      type: 'MULTI_PDF_REPORT_DATA',
      payload: { reportKey, reportData }
    };
    
    this.sendMessageToIframe(message);
  }

  /**
   * Subscribe to load report group events and forward to iframe
   */
  private subscribeLoadReportGroup(): void {
    this.reportsCommonService.loadReportGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (groupData) => {
        if(!groupData || !this.isIframeLoaded) {
          return;
        }

        const savedState = this.reportsCommonService.getLastMultiPdfState();

        // If the group being loaded is a Local Library group and has unsaved changes, prompt the user to save or discard changes
          if(savedState?.groupType === 'local' && savedState?.hasChanges
            && !(savedState.groupType === groupData.groupType && savedState.groupName === groupData.groupName)
          ) {
          const dialogData: ISaveMultiPDFConfirmationDialogData = {
            selectionName: savedState.groupName,
            showCancelButton: true
          };
          const confirmationDialogRef = this.dialog.open(SaveMultiPdfConfirmationDialog, {
            disableClose: true,
            data: dialogData
          });
          const result = await firstValueFrom(confirmationDialogRef.afterClosed());
          if(result === CONSTANTS.YES) {
            const reportKeys = savedState.reports.map(report => report.key);
            await this.reportsCommonService.updateLocalLibraryGroup(savedState.groupName, reportKeys, savedState.description);
            this.sendLoadReportGroupMessage(groupData);
          }
          else if(result === CONSTANTS.NO) {
            this.sendLoadReportGroupMessage(groupData);
          } else{
            const grpId = this.reportsCommonService.getLocalLibraryGroups()
              .find(g => g.name === savedState.groupName)?.id ?? savedState.groupName;
            this.reportsCommonService.notifyReportSwitchCancelled(grpId);
          }
        } 
        else {
          this.sendLoadReportGroupMessage(groupData);
        }
      });
  }

  /**
   * Send message to iframe to load a report group (Standard/Local Library or User Defined)
   */
  private sendLoadReportGroupMessage(groupData: LoadReportGroupPayload): void {
    const message: ReportEnginePayload = {
      type: 'LOAD_REPORT_GROUP',
      payload: groupData
    };
    
    this.sendMessageToIframe(message);
  }

  ngAfterViewInit(): void {
    this.setupMessageListener();
    
    // Wait for iframe to load
    if (this.reportFrame?.nativeElement) {
      this.reportFrame.nativeElement.onload = () => {
        this.isIframeLoaded = true;
        this.shouldRehydrateMultiPdfState = true;
        this.sendReportTranslations();
        
        if (this.pendingReportData) {
          this.loadReportData(this.pendingReportData);
          this.pendingReportData = null;
        } 
        // else {
        //   this.loadReportData(this.sampleReportData);
        // }
      };
    }
  }

  private handleIframeMessage = async (event: MessageEvent): Promise<void> => {
    // Security: Verify origin and Verify the message is from our iframe
    if (event.origin !== location.origin || event.source !== this._reportFrame?.nativeElement?.contentWindow) {
      return;
    }
    
    const { data } = event;

    // Handle PDF rendered notification - stop spinner
    if (data?.type === 'PDF_RENDERED') {
      this.stopReportLoadingSpinner();
      return;
    }

    if (data?.type === 'PDF_RENDER_FAILED') {
      this.stopReportLoadingSpinner();
      return;
    }
    if (data?.type === 'CHECKBOX_STATE_CHANGE' && data.payload) {
      const newCheckboxes: ReportCheckboxes = { ...data.payload };
      
      if (this.hasCheckboxesChanged(this.reportCheckboxes, newCheckboxes)) {
        this.reportCheckboxes = newCheckboxes;
        this.reportsCommonService.setReportCheckboxChanged(this.reportCheckboxes);
      }
    }
    
    // Handle Multi-PDF cancelled - notify other components to restore the selection
    // Note: report-view.component handles regenerating the report via selectReport()
    if (data?.type === 'MULTI_PDF_CANCELLED') {

      const { reportKeys, groupName, description, hasChanges, groupType } = data.payload || {};
      if(groupType === 'local' && hasChanges) {
        const dialogData: ISaveMultiPDFConfirmationDialogData = {
          selectionName: groupName,
          showCancelButton: false
        };
        const confirmationDialogRef = this.dialog.open(SaveMultiPdfConfirmationDialog, {
            disableClose: true,
            data: dialogData
        });
        const result = await firstValueFrom(confirmationDialogRef.afterClosed());
        if (result === CONSTANTS.YES) {
          await this.reportsCommonService.updateLocalLibraryGroup(groupName, reportKeys, description);
        }
        else if (result === CONSTANTS.CANCEL) {
          return;
        }
      }
      this.sendMessageToIframe({
        type: 'CANCEL_CONFIRMED',
        payload: { }
      } as ReportEnginePayload);
      // Set flag to indicate we're restoring from Multi-PDF (preserve checkbox states)
      this.isRestoringFromMultiPdf = true;
      // Notify other components that Multi-PDF mode was cancelled
      this.reportsCommonService.notifyMultiPdfCancelled();
      
    }

    // Handle Generate Multi-PDF request from iframe
    if (data?.type === 'GENERATE_MULTI_PDF' && data.payload?.reportKeys) {
      this.reportsCommonService.requestMultiPdfGeneration(data.payload.reportKeys);
    }
    
    // Handle Save Report Group request from iframe - opens the save dialog
    if (data?.type === 'OPEN_SAVE_DIALOG' && data.payload) {
      this.openSaveMultiPdfDialog(data.payload);
    }

    // Persist current Multi-PDF list/group state for iframe rehydration after tab navigation
    if (data?.type === 'MULTI_PDF_LIST_STATE' && data.payload) {
      this.reportsCommonService.setLastMultiPdfState(data.payload as LoadReportGroupPayload);
    }
    
    // Handle Update Report Group request from iframe
    if (data?.type === 'UPDATE_REPORT_GROUP' && data.payload) {
      this.reportsCommonService.updateLocalLibraryGroup(data.payload.groupName, data.payload.reportKeys, data.payload.description).then(() => {
        // Send confirmation back to iframe
        this.sendSaveConfirmation(data.payload.groupName, data.payload.description || '');
      });
    }
  }
  
  /**
   * Open the Save Multi-PDF dialog
   */
  private openSaveMultiPdfDialog(payload: { reportKeys: string[]; currentGroupName?: string; currentDescription?: string; isUpdate?: boolean }): void {
    // Pre-populate with current values, but don't use 'User Defined' as a default name
    // (it's a reserved category name that would cause selection issues)
    const currentName = payload.currentGroupName || '';
    
    const dialogData: SaveMultiPdfDialogData = {
      selectionName: payload.currentGroupName ?? '',
      description: payload.currentDescription ?? '',
      isUpdate: payload.isUpdate ?? false
    };
    
    const dialogRef = this.dialog.open(SaveMultiPdfDialogComponent, {
      width: '450px',
      data: dialogData,
      disableClose: true
    });
    
    dialogRef.afterClosed().subscribe((result: SaveMultiPdfDialogResult) => {
      if (result) {
        // Save to Local Library (API call)
        this.reportsCommonService.saveToLocalLibrary(result.selectionName, payload.reportKeys, result.description).then(() => {
          // Send confirmation back to iframe with the new group info
          this.sendSaveConfirmation(result.selectionName, result.description);
        });
      }
    });
  }
  
  /**
   * Send save confirmation back to iframe to update header
   */
  private sendSaveConfirmation(groupName: string, description: string): void {
    const message: ReportEnginePayload = {
      type: 'SAVE_CONFIRMED',
      payload: { groupName, description }
    };
    
    this.sendMessageToIframe(message);
  }

  ngOnDestroy(): void {
    // Complete the destroy$ subject to automatically unsubscribe all subscriptions
    this.destroy$.next();
    this.destroy$.complete();
    
    window.removeEventListener('message', this.handleIframeMessage);
    
    if (this._reportFrame?.nativeElement) {
      this._reportFrame.nativeElement.onload = null;
    }

    this.stopReportLoadingSpinner();
  }

  private loadReportData(reportData: IReportData): void {
      // Send report data
      const reportMessage = {
        type: 'REPORT_PDF',
        payload: reportData
      };
      
      this.sendMessageToIframe(reportMessage);

      // Rehydrate Multi-PDF list/group state only after iframe remount.
      // Replaying on every Multi-PDF selection causes previous state to override current click.
      if (reportData.isMultiPdf && this.shouldRehydrateMultiPdfState) {
        this.shouldRehydrateMultiPdfState = false;
        const savedMultiPdfState = this.reportsCommonService.getLastMultiPdfState();
        if (savedMultiPdfState) {
          setTimeout(() => this.sendLoadReportGroupMessage(savedMultiPdfState), 20);
        }
      } else if (!reportData.isMultiPdf) {
        this.shouldRehydrateMultiPdfState = false;
      }
      
      // Restore checkbox states for both standard and Multi-PDF reports.
      // Keep cancel flow behavior by avoiding overrides unless we are in Multi-PDF mode.
      if (!this.isRestoringFromMultiPdf || reportData.isMultiPdf) {
        this.applyCheckboxDisableStateForSelectedReport();
        // Delay avoids a race where report-engine view toggle restores stale states.
        setTimeout(() => {
          this.sendInitialCheckboxStates();
        }, 120);
      }
      
      // Reset the restoration flag
      if (this.isRestoringFromMultiPdf) {
        this.isRestoringFromMultiPdf = false;
      }
  }

  private applyCheckboxDisableStateForSelectedReport(): void {
    this.reportCheckboxes = this.reportsCommonService.getReportCheckboxesForReport();
  }
  
  private sendInitialCheckboxStates(): void {
    const checkboxMessage = {
      type: 'CHECKBOX_INITIAL_STATES',
      payload: this.reportCheckboxes
    };
    
    this.sendMessageToIframe(checkboxMessage);
  }
  
  private setupMessageListener(): void {
    window.addEventListener('message', this.handleIframeMessage);
  }

  private stopReportLoadingSpinner(): void {
    this.reportsCommonService.stopReportLoading();
  }
  
  private hasCheckboxesChanged(current: ReportCheckboxes, newValues: ReportCheckboxes): boolean {
    return current.color.checked !== newValues.color.checked ||
           current.expanded.checked !== newValues.expanded.checked ||
           current.genericName.checked !== newValues.genericName.checked;
  }

  /**
   * Send a message to the iframe - uses service method for better testability
   */
  private sendMessageToIframe(message: ReportEnginePayload): void {
    if (this._reportFrame?.nativeElement?.contentWindow) {
      this.reportsCommonService.sendMessageToIframe(this._reportFrame.nativeElement.contentWindow, message);
    }
  }

  private sendReportTranslations(): void {
    const translations = reportTranslationKeys.reduce((acc, key) => {
      acc[key] = this.translate.instant(key);
      return acc;
    }, {} as Record<string, string>);

    const message: ReportEnginePayload = {
      type: 'SET_TRANSLATIONS',
      payload: { translations }
    };

    this.sendMessageToIframe(message);
  }

  isEstimateOpen(): boolean {
    return this.commonFunctionsService.isEstimateOpen();
  }

}
